/**
 * Sign-in, shared by all three apps.
 *
 * Presentational on purpose — it takes a submit handler and an error string and
 * knows nothing about the API. That keeps the auth flow testable without a
 * network and lets each app decide what happens afterwards (the POS goes to a
 * floor plan, the KDS to a station picker, admin to a dashboard).
 *
 * The tenant field is here because sign-in has to establish *which* tenant
 * before it can check a password. In production this is filled from the
 * subdomain and hidden; on a shared dev host it has to be typed.
 *
 * **Sign-in is two steps, not one.** An account with a second factor answers a
 * correct password with a challenge instead of tokens, and a form that assumes
 * one step leaves that person looking at the screen they just filled in
 * correctly, with nothing to tell them why. So the step lives here, in the one
 * form all three apps render, rather than three times over in three shells:
 * the challenge is read straight out of whatever the sign-in call answered
 * with, and the app only has to say what to do with a code once it is typed.
 *
 * That answer is narrowed structurally — the same trick as `action-error.tsx`,
 * and for the same reason. A challenge is recognisable by its shape, and this
 * package does not depend on the API client and should not start.
 *
 * The challenge token never leaves this component's state. It proves a
 * password was right and nothing else, so it is not a session and has no
 * business in storage, in a URL, or anywhere it would outlive the tab.
 */

import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { Button, Field, Input } from './primitives';
import './sign-in.css';

export interface SignInFormProps {
  appName: string;
  tagline?: string | undefined;
  submitting?: boolean | undefined;
  error?: string | null | undefined;
  defaultTenantSlug?: string | undefined;
  /**
   * Whether to ask which restaurant. True for the three restaurant apps; false
   * for the platform admin, whose operators belong to no restaurant.
   */
  askTenant?: boolean | undefined;
  /** The small line above the title. The platform's name unless told otherwise. */
  eyebrow?: string | undefined;
  onSubmit: (credentials: { tenant_slug: string; email: string; password: string }) => void;
  /**
   * A second way forward under the button, such as "forgot your password?"
   * in the back office. Optional because the till and the pass do without it.
   */
  alternative?: ReactNode | undefined;
  /**
   * Whatever the sign-in call answered with, handed over untouched.
   *
   * A password that was right but needs a second factor comes back as a
   * challenge rather than as tokens, and this form is the only thing that can
   * see that and act on it without the app growing a copy of the flow.
   * Anything that is not a challenge is ignored.
   */
  result?: unknown;
  /**
   * What to do with a typed code. Without it the second step is never shown,
   * so an app that has not wired one up behaves exactly as it did before.
   */
  onSubmitCode?: ((challengeToken: string, code: string) => void) | undefined;
  codeSubmitting?: boolean | undefined;
  /**
   * The failure from the code call, raw. Narrowed here rather than in each
   * app so that "that code is not right" is worded once, next to the field it
   * appears under.
   */
  codeError?: unknown;
  /**
   * Throw the half-finished sign-in away and ask for a password again.
   *
   * Called when they ask for it and when the challenge runs out. The app has
   * to forget the challenge here, or the next render walks straight back into
   * a second step that the server has already stopped accepting.
   */
  onRestart?: (() => void) | undefined;
}

/** A sign-in that stopped half way, recognised by shape rather than by class. */
interface SecondFactor {
  challengeToken: string;
  /** Seconds. Zero when the server did not say, which just means no clock. */
  expiresIn: number;
}

function secondFactorIn(result: unknown): SecondFactor | null {
  if (typeof result !== 'object' || result === null) return null;
  const candidate = result as {
    mfa_required?: unknown;
    challenge_token?: unknown;
    expires_in?: unknown;
  };
  if (candidate.mfa_required !== true) return null;
  if (typeof candidate.challenge_token !== 'string' || candidate.challenge_token === '') {
    return null;
  }
  return {
    challengeToken: candidate.challenge_token,
    expiresIn: typeof candidate.expires_in === 'number' ? candidate.expires_in : 0,
  };
}

/**
 * Why a code was refused, in the words of somebody holding a phone.
 *
 * The server answers a wrong code, a code that has already been used, and a
 * challenge that timed out all alike — so this cannot claim to know which, and
 * says the one thing that is true of all three and can be acted on. The clock
 * on the step below handles the timed-out case before it gets this far.
 */
function codeMessage(error: unknown): string | null {
  if (error === null || error === undefined || error === false) return null;
  const candidate = error as { status?: unknown; code?: unknown; message?: unknown };
  if (candidate.status === 401) {
    return 'That code is not right. Check your app for the six digits showing now, or type one of your recovery codes.';
  }
  if (candidate.code === 'network_unreachable') {
    return 'Cannot reach the server. Check the connection and try again.';
  }
  if (error instanceof Error) return error.message;
  return 'That did not go through. Try the code again.';
}

export function SignInForm({
  appName,
  tagline,
  submitting = false,
  error = null,
  defaultTenantSlug = '',
  askTenant = true,
  eyebrow = 'OnRestaurant',
  onSubmit,
  alternative,
  result,
  onSubmitCode,
  codeSubmitting = false,
  codeError,
  onRestart,
}: SignInFormProps) {
  const [tenantSlug, setTenantSlug] = useState(defaultTenantSlug);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Why they are back on the password step, when they did not ask to be.
  const [timedOut, setTimedOut] = useState(false);
  const errorId = useId();

  const challenge = onSubmitCode ? secondFactorIn(result) : null;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTimedOut(false);
    onSubmit({ tenant_slug: tenantSlug.trim(), email: email.trim(), password });
  }

  if (challenge && onSubmitCode) {
    return (
      // Keyed on the challenge so a second attempt starts with an empty field
      // and a clock of its own, rather than inheriting the last one's.
      <CodeStep
        key={challenge.challengeToken}
        appName={appName}
        expiresIn={challenge.expiresIn}
        submitting={codeSubmitting}
        error={codeMessage(codeError)}
        onSubmitCode={(code) => onSubmitCode(challenge.challengeToken, code)}
        onGiveUp={(ranOut) => {
          setTimedOut(ranOut);
          onRestart?.();
        }}
      />
    );
  }

  return (
    <main className="signin">
      <form className="signin__card" onSubmit={handleSubmit} noValidate>
        <div className="signin__body">
          <header className="signin__head">
            <p className="signin__eyebrow">{eyebrow}</p>
            <h1 className="signin__title">{appName}</h1>
            {tagline && <p className="signin__tagline">{tagline}</p>}
          </header>

          {/* Sent back here by the clock rather than by a tap, so it says so.
              Without this the screen silently resets itself while somebody is
              still reading a code off a phone. */}
          {timedOut && (
            <p className="signin__note" role="status">
              That sign-in timed out while it waited for your code. Enter your password again
              and a fresh code will be asked for.
            </p>
          )}

          {askTenant && (
            <Field label="Restaurant" hint="The short name in your web address">
              <Input
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                autoComplete="organization"
                placeholder="acme-diner"
                required
              />
            </Field>
          )}

          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
            />
          </Field>

          <Field label="Password">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </Field>

          {/* One message for a wrong password and an unknown restaurant, matching
              the backend, which answers both identically so the form cannot be
              used to discover which tenants exist. */}
          {error && (
            <p className="signin__error" id={errorId} role="alert">
              {error}
            </p>
          )}
        </div>

        {/* Outside the scrolling body on purpose: the keyboard covers the
            bottom of a tablet, and the one button on this screen must not be
            what it covers. */}
        <div className="signin__foot">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={submitting}
            aria-describedby={error ? errorId : undefined}
          >
            Sign in
          </Button>
          {alternative}
        </div>
      </form>
    </main>
  );
}

/**
 * The second step: the six digits, or a recovery code.
 *
 * One field takes both. Asking somebody to declare which kind of code they
 * are about to type is asking a question only the server can answer, and the
 * person who reaches for a recovery code is by definition the one having the
 * worst morning — a phone that is flat, lost, or wiped. They are told a
 * recovery code works here, on the screen, because the alternative is finding
 * that out from a help page they cannot open without signing in.
 *
 * **The keyboard is numeric to start with and can be switched.** Six digits on
 * a tablet held one-handed deserve a keypad, but a keypad on iOS has no
 * letters at all, so a field locked to `inputMode="numeric"` would make a
 * recovery code physically impossible to type. `type="number"` would be worse
 * still: it strips the hyphen and the letters on the way in. So the field is
 * plain text, the keypad is a default rather than a rule, and it changes by
 * itself the moment a non-digit arrives — which is what a paste looks like.
 *
 * **The clock is real.** The challenge dies after a few minutes, and the
 * server refuses an expired one with the same 401 as a mistyped code. Somebody
 * who has been hunting for their phone would read that as "wrong code" and
 * retype it, correctly, forever. So this counts down and hands the screen back
 * to the password step itself, saying why.
 */
function CodeStep({
  appName,
  expiresIn,
  submitting,
  error,
  onSubmitCode,
  onGiveUp,
}: {
  appName: string;
  expiresIn: number;
  submitting: boolean;
  error: string | null;
  onSubmitCode: (code: string) => void;
  /** True when the clock ran out rather than when they asked to go back. */
  onGiveUp: (ranOut: boolean) => void;
}) {
  const [code, setCode] = useState('');
  const [lettersWanted, setLettersWanted] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(expiresIn);
  const fieldRef = useRef<HTMLInputElement>(null);

  // Read through a ref so the interval below is not torn down and rebuilt
  // every render by a callback the parent declares inline.
  const giveUp = useRef(onGiveUp);
  useEffect(() => {
    giveUp.current = onGiveUp;
  });

  useEffect(() => {
    if (expiresIn <= 0) return undefined;
    const id = window.setInterval(() => {
      setSecondsLeft((left) => (left > 1 ? left - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [expiresIn]);

  useEffect(() => {
    if (expiresIn > 0 && secondsLeft === 0) giveUp.current(true);
  }, [expiresIn, secondsLeft]);

  // A recovery code pasted in brings its own letters, so the keypad that no
  // longer fits gets out of the way without being asked.
  const digitsOnly = /^\d*$/.test(code);
  const keypad = !lettersWanted && digitsOnly;

  function wantLetters() {
    setLettersWanted(true);
    // iOS keeps whichever keyboard it opened with, so the field has to be let
    // go of and taken again for the change to reach the person typing.
    const field = fieldRef.current;
    if (field) {
      field.blur();
      window.setTimeout(() => field.focus(), 0);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Both kinds of code get read off something and typed, and both arrive
    // with stray spaces in them. A space is never part of either.
    const typed = code.replace(/\s+/g, '');
    if (typed === '') return;
    onSubmitCode(typed);
  }

  return (
    <main className="signin">
      <form className="signin__card" onSubmit={handleSubmit} noValidate>
        <div className="signin__body">
          <header className="signin__head">
            <p className="signin__eyebrow">{appName}</p>
            <h1 className="signin__title">One more step</h1>
            <p className="signin__tagline">
              Your password was right. This account also asks for a code.
            </p>
          </header>

          <Field label="Code" error={error ?? undefined}>
            <Input
              ref={fieldRef}
              className="signin__code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode={keypad ? 'numeric' : 'text'}
              // The browser and the password manager both know this one, and
              // on iOS it is what puts a code from a text message on the
              // keyboard bar. Harmless for an authenticator app.
              autoComplete="one-time-code"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="go"
              autoFocus
              required
            />
          </Field>

          <p className="signin__hint">
            {lettersWanted ? (
              <>
                Type one of your recovery codes — ten characters with a hyphen, like
                ab3kf-9mqx2.
              </>
            ) : (
              <>
                Six digits from your authenticator app. No phone with you?{' '}
                <button type="button" className="signin__switch" onClick={wantLetters}>
                  Use a recovery code
                </button>
                .
              </>
            )}
          </p>

          {/* Only once it matters. A clock counting down from five minutes
              next to an empty field makes a routine sign-in feel like a test;
              a clock that appears in the last minute is information. */}
          {expiresIn > 0 && secondsLeft > 0 && secondsLeft <= 60 && (
            <p className="signin__note" role="status">
              {secondsLeft} second{secondsLeft === 1 ? '' : 's'} left to enter a code. After
              that, sign in with your password again.
            </p>
          )}
        </div>

        <div className="signin__foot">
          <Button type="submit" variant="primary" size="lg" block loading={submitting}>
            Sign in
          </Button>
          <button type="button" className="signin__alt" onClick={() => onGiveUp(false)}>
            Back to your password
          </button>
        </div>
      </form>
    </main>
  );
}
